import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  /** The sentence: what is missing. */
  title: string;
  /** A second line: why, or what to do next. */
  description?: string;
  /**
   * A way forward, when there is one: a Button or a Link the caller owns,
   * carrying its own test ID.
   */
  action?: ReactNode;
  /** Decorative. Hidden from assistive technology; the meaning is in the words. */
  icon?: ReactNode;
  /** `<testId>-empty` on the box. See docs/test-ids.md. */
  testId: string;
}

/**
 * The one shape for "nothing here": a list with no rows, a filter that
 * excluded everything.
 *
 * Content, not an alert: nothing is announced, because nothing went wrong.
 * An outlined box, centred, in muted text - what DataTable's empty branch
 * drew before this existed, so every list keeps its look. It is for the
 * whole space a list would fill; a one-line message inside a card stays a
 * line of text.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  testId,
}: EmptyStateProps) {
  return (
    <Paper
      variant="outlined"
      data-testid={`${testId}-empty`}
      sx={{ p: 6, textAlign: 'center' }}
    >
      {icon ? (
        <Box
          aria-hidden="true"
          sx={{ color: 'text.secondary', mb: 1, '& svg': { fontSize: 40 } }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography color="text.secondary">{title}</Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      ) : null}
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Paper>
  );
}
